import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_size_md,
  font_size_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Vertical padding only. The prototype's row is `padding: 12px 0` — with no
 * horizontal padding the divider starts exactly at the avatar's left edge.
 * Adding side padding pushes the border out past the avatar on both sides,
 * which is visible as an overhanging rule against the Box edge.
 */
export const row = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing(3)};
  padding: ${spacing(3)} 0;
  border-bottom: 1px solid ${base_colorContentBorder};

  &:last-of-type {
    border-bottom: none;
  }
`

/**
 * Flush with the top of the sentence beside it — the design aligns the avatar to
 * the first text line, not to the row's optical centre, so a nudge here shows up
 * as the avatar sitting low against a one-line event.
 */
export const avatar = css`
  flex: 0 0 auto;

  /*
     The ADS spec fixes sm fallback initials at 10px, uppercase, medium.
     ext-ui derives its own size from the avatar box (0.4 x 24 = 9.6px) and uses
     regular weight, so the three properties are pinned here rather than left to
     that derivation.
  */
  & .fallback-text {
    font-size: 10px;
    font-family: ${font_family_weight_medium};
    text-transform: uppercase;
  }
`

/* min-width: 0 so a long server-authored message wraps instead of forcing the
   row wider than its container. */
export const content = css`
  min-width: 0;
  flex: 1;
`

/**
 * One sentence, one text flow. Actor, verb, and detail are deliberately NOT
 * separate blocks: the design emphasizes only the actor and lets the rest wrap
 * as continuous prose, so the emphasis is a span inside this line rather than a
 * layout boundary.
 */
export const sentence = css`
  font-size: ${font_size_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};
`

/**
 * The only emphasized phrase in the row. `medium` is the heaviest text weight
 * the design system exposes, and the design asks for exactly that — do not
 * reach for semibold/bold to make it "read better".
 */
export const actor = css`
  font-family: ${font_family_weight_medium};
`

export const detail = css`
  color: ${text_colorTextSecondary};
`

/* Sits between the sentence and the timestamp, inline-flex so the Pill does not
   stretch to the row's full width. */
export const outcome = css`
  display: inline-flex;
  margin-top: ${spacing(1)};
`

export const timestamp = css`
  display: block;
  /* 2px, per the prototype — spacing(1) is 4px and reads as a gap rather than
     the tight caption the design pairs with the sentence above it. */
  margin-top: 2px;
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`
